import apiClient from '@/api/client';

// ============================================================
// Types
// ============================================================

export interface MessagingSettings {
  id: string;
  company_id: string;
  ai_provider: string;
  ai_config: Record<string, string>;
  sms_provider: string;
  sms_config: Record<string, string>;
  scheduler_type: string;
  scheduler_config: Record<string, string>;
  greeting_time: string;
  whatsapp_group_id: string | null;
  announcement_recipients: string[];
  image_provider: string;
  image_provider_config: Record<string, string>;
  whatsapp_provider: string;
  whatsapp_config: Record<string, string>;
  email_provider: string;
  email_config: Record<string, any>;
  greeting_rules: Record<string, any>;
  is_active: boolean;
  webhook_id?: string | null;
  timezone_id?: string;
  created_at: string;
  updated_at: string;
}

export interface MessageTemplate {
  id: string;
  company_id: string;
  category: string;
  name: string;
  subject: string | null;
  body_template: string;
  language: string;
  is_default: boolean;
  is_active: boolean;
  sort_order: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface GreetingCardTemplate {
  id: string;
  company_id: string;
  category: string;
  name: string;
  background_url: string | null;
  text_position_x: number;
  text_position_y: number;
  font_name: string;
  font_size: number;
  font_color: string;
  photo_position_x: number;
  photo_position_y: number;
  photo_size: number;
  overlay_color: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface GreetingLogEntry {
  id: string;
  company_id: string;
  employee_id: string;
  employee_name: string | null;
  greeting_type: string;
  channels_sent: Record<string, boolean>;
  card_image_url: string | null;
  message_text: string | null;
  status: string;
  error_message: string | null;
  sent_at: string;
}

export interface MessageLogEntry {
  id: string;
  direction: string;
  phone_number: string;
  message_type: string;
  content: string;
  status: string;
  error_message: string | null;
  created_at: string;
}

export interface MessageDraft {
  id: string;
  employee_ids: string[];
  channels: Record<string, boolean>;
  message_text: string;
  email_subject: string | null;
  template_id: string | null;
  greeting_type: string | null;
  card_template_id: string | null;
  send_to_group: boolean;
  scheduled_at: string | null;
  status: string;
  sent_at: string | null;
  send_result: Record<string, unknown> | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface UpcomingGreeting {
  employee_id: string;
  employee_name: string;
  employee_ref: string;
  designation: string | null;
  department_name: string | null;
  event_type: string;
  event_date: string;
  days_until: number;
  years: number | null;
  profile_photo: string | null;
  phone: string | null;
  email: string | null;
}

// ============================================================
// API Client
// ============================================================

export const messagingApi = {
  // Provider Registry (dynamic — no hardcoded lists on frontend)
  getProviders: () =>
    apiClient.get<Record<string, any[]>>('/messaging/providers'),

  // Settings
  getSettings: () =>
    apiClient.get<MessagingSettings>('/messaging/settings'),
  updateSettings: (data: Record<string, unknown>) =>
    apiClient.put<MessagingSettings>('/messaging/settings', data),
  // Per-tenant inbound webhook URLs — tenant pastes these into Meta,
  // Trigger.dev, SendGrid, Postmark admin consoles. Each URL embeds this
  // company's webhook_id so we resolve the tenant before signature verify.
  getWebhookUrls: () =>
    apiClient.get<{
      webhook_id: string;
      whatsapp: string;
      trigger: string;
      email: string;
      email_events: string;
      twilio_status: string;
    }>('/messaging/settings/webhook-urls'),
  // One-shot sidebar badge counts (replaces 3 separate queries).
  getCounts: () =>
    apiClient.get<{
      drafts: number;
      scheduled: number;
      pending_approval: number;
      unread: number;
    }>('/messaging/counts'),
  // WhatsApp HSM templates (per-tenant)
  listWhatsAppTemplates: (params?: { status?: string; active_only?: boolean }) =>
    apiClient.get<Array<{
      id: string;
      company_id: string;
      name: string;
      language: string;
      category: string;
      status: string;
      meta_template_id: string | null;
      body_text: string;
      components: any[];
      submitted_at: string | null;
      approved_at: string | null;
      last_synced_at: string | null;
      rejection_reason: string | null;
      is_active: boolean;
    }>>('/messaging/whatsapp-templates', { params }),
  createWhatsAppTemplate: (data: {
    name: string;
    language?: string;
    category?: string;
    body_text: string;
    components?: any[];
    status?: string;
    meta_template_id?: string;
  }) => apiClient.post('/messaging/whatsapp-templates', data),
  updateWhatsAppTemplate: (id: string, data: Record<string, unknown>) =>
    apiClient.patch(`/messaging/whatsapp-templates/${id}`, data),
  deleteWhatsAppTemplate: (id: string) =>
    apiClient.delete(`/messaging/whatsapp-templates/${id}`),
  syncWhatsAppTemplates: () =>
    apiClient.post<{ synced: number; errors: string[] }>('/messaging/whatsapp-templates/sync'),
  seedStarterWhatsAppTemplates: () =>
    apiClient.post<{ created: number; skipped: number }>(
      '/messaging/whatsapp-templates/seed-starters',
    ),
  sendWhatsAppAnnouncement: (data: {
    template_name: string;
    template_language?: string;
    template_params?: string[];
    department_id?: string;
    employment_status?: string;
    employee_ids?: string[];
  }) =>
    apiClient.post<{
      sent: number;
      failed: number;
      skipped: number;
      total_audience: number;
      errors: Array<{ employee_id: string; error: string }>;
    }>('/messaging/announcements/whatsapp', data),

  replyToInbox: (messageId: string, data: { body: string; subject?: string }) =>
    apiClient.post<{
      id: string;
      channel: string;
      sent_to: string;
      provider_message_id?: string;
    }>(`/messaging/inbox/${messageId}/reply`, data),
  listInboxThreads: (params?: { channel?: string; assignee?: string; page?: number; per_page?: number }) =>
    apiClient.get<{
      items: Array<{
        channel: string;
        from_address: string;
        from_name: string | null;
        last_at: string;
        last_preview: string;
        last_subject: string | null;
        message_count: number;
        unread_count: number;
      }>;
      total: number;
      page: number;
      per_page: number;
    }>('/messaging/inbox/threads', { params }),
  getInboxThread: (channel: string, fromAddress: string) =>
    apiClient.get<{
      channel: string;
      from_address: string;
      items: Array<{
        id: string;
        direction: 'inbound' | 'outbound';
        channel: string;
        from_address: string | null;
        from_name: string | null;
        subject: string | null;
        content: string;
        content_type: string;
        status: string;
        is_read: boolean;
        created_at: string;
      }>;
    }>(
      `/messaging/inbox/threads/${channel}/${encodeURIComponent(fromAddress)}`
    ),
  testSMS: (data: { phone: string; message?: string }) =>
    apiClient.post('/messaging/settings/test-sms', data),
  testWhatsApp: (data: { phone: string; message?: string }) =>
    apiClient.post('/messaging/settings/test-whatsapp', data),
  testAI: (data: { employee_name?: string; category?: string }) =>
    apiClient.post<{ greeting_text: string; card_text: string }>('/messaging/settings/test-ai', data),
  testEmail: (data: { to?: string; subject?: string; message?: string }) =>
    apiClient.post<{ status: string; to?: string; message?: string }>('/messaging/settings/test-email', data),
  testSmtp: () =>
    apiClient.post<{ status: string; message?: string; host?: string; port?: number }>('/messaging/settings/test-smtp'),
  verifyWhatsAppToken: () =>
    apiClient.post<{ ok: boolean; message: string; detail?: string; provider?: string; me?: any; phone?: any; fix?: string; token_length?: number }>('/messaging/settings/verify-whatsapp-token'),
  // Group-level shared SMTP
  getGroupEmail: () =>
    apiClient.get<{
      available: boolean;
      group_id?: string;
      email_provider?: string;
      email_config?: Record<string, any>;
      last_verified_at?: string;
      reason?: string;
    }>('/messaging/group-settings/email'),
  saveGroupEmail: (data: { email_provider?: string; email_config: Record<string, any> }) =>
    apiClient.put<{ ok: boolean; message: string; email_config?: Record<string, any>; group_id?: string }>('/messaging/group-settings/email', data),
  applyGroupEmail: () =>
    apiClient.post<{ ok: boolean; message: string; email_provider?: string }>('/messaging/settings/apply-group-email'),

  // Message Templates
  listTemplates: (params?: { category?: string; language?: string }) =>
    apiClient.get<MessageTemplate[]>('/messaging/templates', { params }),
  createTemplate: (data: Record<string, unknown>) =>
    apiClient.post<MessageTemplate>('/messaging/templates', data),
  updateTemplate: (id: string, data: Record<string, unknown>) =>
    apiClient.patch<MessageTemplate>(`/messaging/templates/${id}`, data),
  deleteTemplate: (id: string) =>
    apiClient.delete(`/messaging/templates/${id}`),

  // Card Templates
  listCardTemplates: (params?: { category?: string }) =>
    apiClient.get<GreetingCardTemplate[]>('/messaging/card-templates', { params }),
  createCardTemplate: (data: Record<string, unknown>) =>
    apiClient.post<GreetingCardTemplate>('/messaging/card-templates', data),
  updateCardTemplate: (id: string, data: Record<string, unknown>) =>
    apiClient.patch<GreetingCardTemplate>(`/messaging/card-templates/${id}`, data),

  // Sending
  sendMessage: (data: Record<string, unknown>) =>
    apiClient.post('/messaging/send', data),
  sendGreeting: (data: Record<string, unknown>) =>
    apiClient.post('/messaging/send/greeting', data),
  sendBulk: (data: Record<string, unknown>) =>
    apiClient.post('/messaging/send/bulk', data),

  // Greetings
  getUpcoming: (days?: number) =>
    apiClient.get<UpcomingGreeting[]>('/messaging/greetings/upcoming', { params: { days } }),
  runNow: () =>
    apiClient.post<{ sent: number; failed: number; total: number }>('/messaging/greetings/run-now'),
  getLog: (params?: { greeting_type?: string; status?: string; page?: number; per_page?: number }) =>
    apiClient.get<{ items: GreetingLogEntry[]; total: number }>('/messaging/greetings/log', { params }),

  // WhatsApp/SMS message logs
  getMessageLog: (params?: { direction?: string; status?: string; page?: number; per_page?: number }) =>
    apiClient.get<{ items: MessageLogEntry[]; total: number }>('/messaging/messages/log', { params }),

  // Sent today (idempotency check)
  getSentToday: () =>
    apiClient.get<{ employee_id: string; greeting_type: string; status: string }[]>('/messaging/greetings/sent-today'),

  // Card Preview
  previewCard: (data: Record<string, unknown>) =>
    apiClient.post('/messaging/card/preview', data, { responseType: 'blob' }),

  // Signoff options
  getSignoffOptions: () =>
    apiClient.get<string[]>('/messaging/templates/signoff-options'),

  // Seed default templates
  seedDefaults: () =>
    apiClient.post<{ messages_created: number; cards_created: number; skipped_categories: string[]; signoff_options: string[] }>('/messaging/templates/seed-defaults'),

  // Drafts & Scheduled Sends
  listDrafts: (params?: { status?: string; page?: number; per_page?: number }) =>
    apiClient.get<{ items: MessageDraft[]; total: number }>('/messaging/drafts', { params }),
  getDraft: (id: string) =>
    apiClient.get<MessageDraft>(`/messaging/drafts/${id}`),
  createDraft: (data: Record<string, unknown>) =>
    apiClient.post<{ id: string; status: string; scheduled_at: string | null }>('/messaging/drafts', data),
  updateDraft: (id: string, data: Record<string, unknown>) =>
    apiClient.patch<{ id: string; status: string }>(`/messaging/drafts/${id}`, data),
  sendDraft: (id: string) =>
    apiClient.post<{ id: string; status: string; result: Record<string, unknown> }>(`/messaging/drafts/${id}/send`),
  deleteDraft: (id: string) =>
    apiClient.delete(`/messaging/drafts/${id}`),

  // Inbox (inbound messages)
  getInbox: (params?: { channel?: string; status?: string; is_read?: boolean; assignee?: string; page?: number; per_page?: number }) =>
    apiClient.get<{ items: any[]; total: number }>('/messaging/inbox', { params }),
  updateInboxMessage: (id: string, data: { is_read?: boolean; status?: string }) =>
    apiClient.patch(`/messaging/inbox/${id}`, data),
  getUnreadCount: () =>
    apiClient.get<{ unread: number; mine: number; unassigned: number }>('/messaging/inbox/unread-count'),
  assignInboxMessage: (id: string, userId?: string) =>
    apiClient.post<{ id: string; assigned_to_user_id: string; is_self_claim: boolean }>(
      `/messaging/inbox/${id}/assign`,
      { user_id: userId ?? null }
    ),
  unassignInboxMessage: (id: string) =>
    apiClient.post<{ id: string; assigned_to_user_id: null }>(`/messaging/inbox/${id}/unassign`),

  // User Preferences
  getPreferences: () =>
    apiClient.get<{ user_id: string; default_channels: Record<string, boolean>; signature: string | null; default_greeting_type: string | null }>('/messaging/preferences'),
  updatePreferences: (data: { default_channels?: Record<string, boolean>; signature?: string; default_greeting_type?: string }) =>
    apiClient.put('/messaging/preferences', data),
};
